import { useAuthStore } from '@shared/stores/authStore';

interface ErrorBody {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
  limit?: number | null;
  current?: number | null;
}

export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;
  limit?: number | null;
  current?: number | null;

  constructor(status: number, body: ErrorBody) {
    super(body.error);
    this.name = 'ApiClientError';
    this.code = body.code ?? 'UNKNOWN';
    this.status = status;
    this.details = body.details;
    this.limit = body.limit;
    this.current = body.current;
  }
}

function getForbiddenMessage(code?: string, body?: ErrorBody): string {
  switch (code) {
    case 'PLAN_LIMIT_REACHED':
      return body?.limit != null
        ? `Plan limit reached: ${body.current}/${body.limit}. Upgrade in Bitrix24 Marketplace.`
        : "You've reached your plan limit. Upgrade to continue.";
    case 'SUBSCRIPTION_EXPIRED':
      return 'Subscription expired. Renew in Bitrix24 Marketplace to continue.';
    case 'APP_NOT_INSTALLED':
      return 'This app is not installed on your Bitrix24 portal.';
    case 'APP_UNINSTALLED':
      return 'This app was uninstalled from your portal. Please reinstall from Bitrix24 Marketplace.';
    case 'APP_SUSPENDED':
      return 'Portal connection lost. Please reinstall the app from Bitrix24 Marketplace.';
    default:
      return "Access denied. You don't have permission for this action.";
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = useAuthStore
      .getState()
      .refreshTokens()
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const { token } = useAuthStore.getState();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && token) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      const newToken = useAuthStore.getState().token;
      headers.set('Authorization', `Bearer ${newToken}`);
      const retryRes = await fetch(url, { ...options, headers });
      if (retryRes.ok || retryRes.status === 202) {
        return retryRes.json() as Promise<T>;
      }
      if (retryRes.status === 401) {
        useAuthStore.getState().logout();
      }
      const body = await retryRes
        .json()
        .catch(() => ({ error: 'Request failed', code: 'UNKNOWN' }));
      if (retryRes.status === 403) {
        body.error = getForbiddenMessage(body.code, body);
      }
      throw new ApiClientError(retryRes.status, body);
    }
    useAuthStore.getState().logout();
    throw new ApiClientError(401, { error: 'Session expired', code: 'AUTH_EXPIRED' });
  }

  if (!res.ok && res.status !== 202) {
    const body = await res.json().catch(() => ({ error: 'Request failed', code: 'UNKNOWN' }));
    if (res.status === 403) {
      body.error = getForbiddenMessage(body.code);
    }
    throw new ApiClientError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(url: string) => request<T>(url),
  post: <T>(url: string, body?: unknown) =>
    request<T>(url, {
      method: 'POST',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  put: <T>(url: string, body?: unknown) =>
    request<T>(url, {
      method: 'PUT',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(url: string) => request<T>(url, { method: 'DELETE' }),
};
